/**
 * messageProjection — turn EventLog entries into chat-bubble messages
 * for the Variant D chat surface.
 *
 * Design Spec Variant D §3.1 defines three message kinds (user,
 * assistant, tool). Our EventLog (Spec 17 §4.1) is a richer enum —
 * it carries audit-grade events that aren't conversational (writer
 * Word edits, approval clicks, library swaps from outside chat).
 *
 * This module is the projection between the two vocabularies. It is
 * the ONLY place in the addin where the question "does this event
 * belong in the chat scroll?" is answered.
 *
 * SRP: pure data transformation. No React, no storage, no I/O. Every
 * branch is exhaustively type-checked against the EventKind union —
 * adding a new kind to ``eventLog.ts`` will fail to compile here
 * until the projection rule is declared (closed-enum discipline).
 *
 * # DECISION: Inclusive projection (Phase 8.1, 2026-04-27) —
 * #   EVERY EventLog kind produces at least one chat message. UI
 * #   events (``word``, ``approval``) render as ``system``-role
 * #   notes (centred, muted, mono); the rest render as
 * #   user/assistant/tool per their semantic.
 * # WHY: writer feedback (2026-04-27) — "I want everything in the
 * #      chat as history. Even the spine run has to be shown (in
 * #      a concise manner) in chat history. Even approval also we
 * #      show in chat history." The chat IS the audit trail in
 * #      friendly form. ``/filter`` and ``/replay`` then scope
 * #      ALL activity (chat + run + UI) without a parallel log.
 * # SUPERSEDES: the prior strict-projection rule (2026-04-26)
 * #   which dropped ``word`` and ``approval``. Strict projection
 * #   was correct for the chat-only mental model; inclusive
 * #   projection is correct for the chat-as-audit mental model.
 * # DATE: 2026-04-27
 *
 * # DECISION: ``askwhy`` events fan out to TWO messages when an
 * #   answer is present (user question + assistant answer); one
 * #   message (just the question) when the answer is still pending.
 * # WHY: a chat surface that shows "you asked X" without "Asthra
 * #      answered Y" is broken. The two are inseparable in the chat
 * #      mental model even though they share a single EventLog
 * #      entry. The assistant message is timestamped at ``ts + 1``
 * #      so chronological ordering is stable across the pair.
 * # DATE: 2026-04-26
 *
 * # DECISION: Synthesised text for ``draft`` / ``regen`` / ``web`` /
 * #   ``cite`` / ``library`` is intentionally terse and templated.
 * # WHY: rich descriptive text would require duplicating the formatTitle
 * #      logic that already lives in TimelineView. The chat surface
 * #      shows the gist; writers who want detail click into the
 * #      Inspector / Spine. YAGNI — wait for a writer to ask for
 * #      richer chat copy before adding it.
 * # DATE: 2026-04-26
 */

import type { MentionAttachment } from "../types";
import { getToolDisplay } from "../config/toolDisplayNames";
import type { EventLogEntry } from "./eventLog";
import { sanitizeDraftForWord } from "./services/draftSanitizer";

// ---------------------------------------------------------------------------
// Public types — the ChatMessage union the Variant D MessageBubble renders
// ---------------------------------------------------------------------------

/**
 * Common run-scope tag carried by every ChatMessage variant.
 *
 *   * a string — the run this message belongs to (visible only when
 *     that run is loaded)
 *   * ``null`` — session-level (greeting, library/doctype selection,
 *     anything authored before a run was active); visible across all runs
 *   * absent (legacy / unprojected) — treated as session-level by both
 *     the client filter and the server filter
 *
 * Set at projection time from ``EventLogEntry.runId``, which the
 * reducer populates from the current ``state.runId`` at append time.
 */
interface ChatMessageRunScope {
  run_id?: string | null;
  /** Stable backend-assigned identity. Present after the first server
   *  round-trip; absent on messages that have never been persisted. */
  _id?: string;
}

/** Snapshot of the message being replied to, embedded at send-time. */
export interface QuotedMessage {
  _id: string;
  role: "user" | "assistant" | "tool" | "system";
  at: number;
  /** Full text of the original message (or name + summary for tool messages). */
  text: string;
}

/** Writer-authored chat message — right-aligned, no bubble. */
export interface UserChatMessage extends ChatMessageRunScope {
  role: "user";
  /** Epoch ms — same scale as EventLogEntry.ts. */
  at: number;
  text: string;
  /** Selected text the writer had highlighted in Word when they sent this
   *  message. Stored so the Timeline can annotate the sent message with
   *  a "📌 context used" indicator. Undefined when nothing was selected. */
  selectionContext?: string;
  /** Message the writer is replying to. Snapshot embedded at send-time so it
   *  survives FIFO eviction of the original message. */
  quotedMessage?: QuotedMessage;
  /** Intent classified at submit (routing on unless the
   *  chat_intent_routing_disabled kill switch is set).
   *  Rendered as a chip next to the "You · HH:MM" label so the
   *  routed action stays visible in the thread. */
  intent?: import("../api/client").ChatRoutedIntent;
  /** @-mentioned source sections/tables, in citation order (index + 1 ==
   *  the ``@[n]`` marker number in ``text``). Lets the bubble render each
   *  marker as an asset chip (icon + truncated name) instead of a bare
   *  ``[n]``. Absent when the message tagged nothing. */
  attachments?: MentionAttachment[];
}

/** Section binding for an insert-ready proposal (chat "draft content"
 *  intent). When present on an assistant message, the Insert action
 *  routes through proposalInsert (section-targeted Word write + SSOT
 *  write-back) instead of the cursor-position insert. */
export interface ProposalInsertTarget {
  runId: string | null;
  moduleId: string;
  sectionNumber: string | null;
}

/** Asthra-authored prose response — left-aligned, brand-tinted bubble. */
export interface AssistantChatMessage extends ChatMessageRunScope {
  role: "assistant";
  at: number;
  text: string;
  /** Error message (e.g., from failed audit). Exclude from reply actions. */
  isError?: boolean;
  /** Present on ad-hoc draft proposals — binds Insert to a section. */
  insertTarget?: ProposalInsertTarget;
  /** Present on decided edit-highlight proposals — renders the
   *  outcome footer ("Applied" / "Rejected") and suppresses the
   *  Insert hover action (the decision already happened). */
  editDecision?: "accepted" | "rejected";
  /** Intent that produced this message (e.g. ``edit_highlight`` on a
   *  proposal). Rendered as a chip next to the "Asthra · HH:MM" label. */
  intent?: import("../api/client").ChatRoutedIntent;
}

/** Tool invocation / state change — full-width dashed strip. */
export interface ToolChatMessage extends ChatMessageRunScope {
  role: "tool";
  at: number;
  /** Tool identifier rendered in brand colour (e.g. ``internet_search``). */
  name: string;
  /** One-line summary of what the tool did or is doing. */
  summary: string;
  /** Optional right-aligned mono meta (e.g. ``2.1s``, ``3 hits``). */
  meta?: string;
  /** Visual status. ``done`` is the default; ``running`` shows the
   *  tool-running dot; ``error`` reserved for future use. */
  status?: "running" | "done" | "error";
}

/** UI-action note rendered as centred mono text. Used for events
 *  that aren't conversation but should still be visible in the chat
 *  audit trail — writer Word edits, approval clicks, run brackets,
 *  etc. Phase 8.1 inclusive-projection role. */
export interface SystemChatMessage extends ChatMessageRunScope {
  role: "system";
  at: number;
  text: string;
}

export type ChatMessage = UserChatMessage | AssistantChatMessage | ToolChatMessage | SystemChatMessage;

// ---------------------------------------------------------------------------
// Projection — the single conversion function
// ---------------------------------------------------------------------------

/**
 * Project one EventLog entry into 0..N chat messages.
 *
 *   * Returns ``[]`` when the event is not conversational (``word``,
 *     ``approval``) — the caller's chat scroll skips it.
 *   * Returns 1 message for the common case (``prompt``, ``draft``,
 *     ``regen``, ``web``, ``cite``, ``library``).
 *   * Returns 2 messages for ``askwhy`` when both halves of the Q&A
 *     are present (writer question + Asthra answer).
 *
 * Pure and total — every EventKind is handled or returns ``[]``.
 * The exhaustive default branch is unreachable when the input is a
 * well-formed entry.
 */
export function projectToMessages(entry: EventLogEntry): ChatMessage[] {
  const messages = projectToMessagesInner(entry);
  // Stamp every projected message with the entry's run scope so the
  // chat surface can filter to a single run on load. Only stamp when
  // a real run is attached — leaving the field absent for null entries
  // keeps them indistinguishable from legacy session-level rows (both
  // are visible across runs) and avoids adding a noisy ``run_id: null``
  // to every greeting / selection message in the persisted blob.
  if (entry.runId) {
    for (const msg of messages) {
      msg.run_id = entry.runId;
    }
  }
  return messages;
}

function projectToMessagesInner(entry: EventLogEntry): ChatMessage[] {
  const { ts, payload } = entry;
  // Phase 9 — single resolution chain for the section display string.
  // Producers populate ``sectionLabel`` (typically ``§${number}``) at
  // the write boundary; we prefer it over the raw ``sectionId``
  // (which is a backend identifier like ``"FRM-607"`` writers don't
  // recognise). Final fallback is the per-case generic word.
  const sectionFor = (fallback: string): string => entry.sectionLabel ?? entry.sectionId ?? fallback;

  switch (payload.kind) {
    case "prompt":
      return [
        {
          role: "user",
          at: ts,
          text: payload.text,
          selectionContext: payload.selectionContext,
          quotedMessage: payload.quotedMessage,
          intent: payload.intent,
          attachments: payload.attachments,
        },
      ];

    case "askwhy": {
      // Only project the assistant answer message. The question is already
      // projected by the earlier "prompt" event. This aligns with the design
      // documented in useChatBridge.ts header.
      if (payload.answer && payload.answer.length > 0) {
        return [{ role: "assistant", at: ts, text: payload.answer, intent: payload.intent }];
      }
      return [];
    }

    case "draft": {
      const section = sectionFor("section");
      const wc = payload.wordCount;
      const header = wc != null ? `Drafted ${section} (${wc} words).` : `Drafted ${section}.`;
      const cleaned = payload.content ? sanitizeDraftForWord(payload.content) : "";
      if (cleaned) {
        // Emit the header as a system note and the content as an
        // assistant message so the full draft is readable in the
        // chat timeline without displacing the brief audit label.
        return [
          { role: "system", at: ts, text: header },
          { role: "assistant", at: ts + 1, text: cleaned },
        ];
      }
      return [{ role: "assistant", at: ts, text: header }];
    }

    case "regen": {
      const section = sectionFor("section");
      const text = `Regenerated ${section} (attempt ${payload.attempt}) — ${payload.outcome}.`;
      return [{ role: "assistant", at: ts, text }];
    }

    case "adhoc_draft": {
      const section = sectionFor("section");

      // DECIDED proposal (Approve/Reject flow): the pending state is
      // transient hook state rendered by AdhocProposalMessage; only the
      // decision reaches the log — mirror the edit_proposal projection.
      // One assistant bubble with the drafted section + outcome; the
      // paired ``approval`` system note (dispatched at ts+1 by the
      // shell) carries the audit prose. No insertTarget — the decision
      // already applied (or discarded) the content.
      if (payload.decision) {
        const cleanedDecided = payload.content ? sanitizeDraftForWord(payload.content) : "";
        const verb = payload.decision === "accepted" ? "approved" : "rejected";
        const isRedraft = payload.mode === "redraft";
        const isEdit = payload.mode === "edit";
        const header = isRedraft
          ? `Redraft for ${section} — ${verb}.`
          : isEdit
            ? `Edit for ${section} — ${verb}.`
            : `Ad-hoc draft for ${section} — ${verb}.`;
        if (cleanedDecided) {
          return [
            { role: "system", at: ts, text: header },
            {
              role: "assistant",
              at: ts + 1,
              text: cleanedDecided,
              editDecision: payload.decision,
              // Redrafts have no routed intent anymore (/regen is a
              // command) — no chip; the header carries the label.
              intent: isRedraft ? undefined : isEdit ? "edit_section" : "draft_content",
            },
          ];
        }
        return [{ role: "system", at: ts, text: header }];
      }

      // LEGACY (pre-decision-flow history): an insert-ready PROPOSAL
      // bound to a section. The assistant bubble carries
      // ``insertTarget`` so its Insert action writes the section in
      // Word AND records the edit in the SSOT (proposalInsert service),
      // instead of the default insert-at-cursor. Header is a system
      // note for the audit trail.
      const wc = payload.wordCount;
      const header =
        wc != null
          ? `Drafted ad-hoc content for ${section} (${wc} words) — review and Insert to apply.`
          : `Drafted ad-hoc content for ${section} — review and Insert to apply.`;
      const cleaned = payload.content ? sanitizeDraftForWord(payload.content) : "";
      const moduleId = payload.moduleId ?? entry.sectionId ?? null;
      // Section number recovered from the canonical "§<number>" label.
      const sectionNumber = entry.sectionLabel?.startsWith("§") ? entry.sectionLabel.slice(1) : null;
      if (cleaned && moduleId) {
        return [
          { role: "system", at: ts, text: header },
          {
            role: "assistant",
            at: ts + 1,
            text: cleaned,
            insertTarget: {
              runId: entry.runId ?? null,
              moduleId,
              sectionNumber,
            },
          },
        ];
      }
      if (cleaned) {
        return [
          { role: "system", at: ts, text: header },
          { role: "assistant", at: ts + 1, text: cleaned },
        ];
      }
      return [{ role: "assistant", at: ts, text: header }];
    }

    case "edit_proposal": {
      // Chat "edit highlight" intent — a DECIDED proposal (the pending
      // state is transient hook state rendered by EditProposalMessage;
      // it never reaches the log). One assistant message carrying the
      // drafted replacement + outcome; the paired ``approval`` system
      // note (dispatched at ts+1 by the shell) carries the audit prose.
      const cleaned = sanitizeDraftForWord(payload.replacementText);
      return [
        {
          role: "assistant",
          at: ts,
          text: cleaned,
          editDecision: payload.decision,
          intent: "edit_highlight",
        },
      ];
    }

    case "web":
      return [
        {
          role: "tool",
          at: ts,
          name: "internet_search",
          summary: payload.query,
          status: "done",
        },
      ];

    case "chat_tool_step":
      // Resolved chat tool step — one persistent activity row per tool
      // the agent ran during the turn. Display name comes from the
      // toolDisplayNames SSOT so this row and the live status line
      // never drift apart.
      return [
        {
          role: "tool",
          at: ts,
          name: getToolDisplay(payload.tool).label,
          summary: payload.inputSummary || payload.resultPreview || "",
          meta: payload.durationMs != null ? formatStepDuration(payload.durationMs) : undefined,
          status: payload.status,
        },
      ];

    case "cite": {
      const summary =
        payload.from && payload.to
          ? `${payload.from} → ${payload.to}`
          : (payload.to ?? payload.from ?? "(citation updated)");
      return [
        {
          role: "tool",
          at: ts,
          name: "citation.swap",
          summary,
          status: "done",
        },
      ];
    }

    case "library":
      return [
        {
          role: "tool",
          at: ts,
          name: "library.update",
          summary: librarySummaryFromPayload(payload),
          status: "done",
        },
      ];

    case "word": {
      // Phase 8.1 — writer's direct Word edit. Inclusive projection:
      // surfaces in chat as a system note so the audit trail stays
      // complete. Spine + Inspector still own the rich detail.
      const section = sectionFor("the document");
      const pc = payload.paragraphCount;
      const text =
        pc != null
          ? `You edited ${section} — ${pc} paragraph${pc === 1 ? "" : "s"} in Word`
          : `You edited ${section} in Word`;
      return [{ role: "system", at: ts, text }];
    }

    case "approval": {
      // Phase 8.1 — writer's approve/reject click on a section.
      // System note in chat; Inspector still owns the verdict
      // detail. ``decision`` is one of accepted/rejected/edited.
      const section = sectionFor("section");
      const verb =
        payload.decision === "accepted" ? "Approved" : payload.decision === "rejected" ? "Rejected" : "Edited";
      const text = payload.note ? `${verb} ${section} — ${payload.note}` : `${verb} ${section}`;
      return [{ role: "system", at: ts, text }];
    }

    case "run_start":
      // Phase 8.5 — run lifecycle bracket. System note marks the
      // moment drafting began; pairs with ``run_complete`` later.
      return [{ role: "system", at: ts, text: "Run started" }];

    case "run_complete": {
      // Phase 8.5 — run lifecycle close bracket. The text combines
      // whichever summary fields the producer captured; missing
      // fields are dropped silently rather than rendering "0 / 0".
      const text = formatRunCompleteText(payload);
      return [{ role: "system", at: ts, text }];
    }

    case "doctype_selected":
      // Phase 9.5 (Bundle A) — fan out to {user-said, asthra-acked}
      // tuple. The writer's pick was implicit (modal click); we
      // synthesise the user line so the chat reads as an exchange
      // rather than a one-sided announcement.
      return [
        { role: "user", at: ts, text: `Selected ${payload.label}.` },
        {
          role: "assistant",
          at: ts + 1,
          text: formatDoctypeAck(payload.label),
        },
      ];

    case "library_selected":
      // Phase 9.5 (Bundle A) — same tuple shape as doctype_selected.
      // ``created`` differentiates "Created X" from "Selected X".
      return [
        {
          role: "user",
          at: ts,
          text: payload.created
            ? `Created library "${payload.libraryName}".`
            : `Selected library "${payload.libraryName}".`,
        },
        {
          role: "assistant",
          at: ts + 1,
          text: formatLibraryAck(payload.libraryName, !!payload.created),
        },
      ];

    case "agent_note":
      // Phase 9.5 (Bundle B+D) — Asthra-emitted prose. Pure
      // pass-through: ``commandSuggester`` rendered the text so the
      // projection has no copy decisions to make. Renders as a
      // standard assistant message (left-aligned brand bubble).
      return [{ role: "assistant", at: ts, text: payload.text }];

    case "audit":
      // Regulatory audit progress is section-scoped and shown in the
      // review tab + spine. Only the terminal phases surface in chat as a
      // brief assistant note; intermediate steps stay out of the timeline.
      return payload.phase === "complete" || payload.phase === "error"
        ? [
            {
              role: "assistant",
              at: ts,
              text: payload.message,
              isError: payload.phase === "error",
            },
          ]
        : [];

    default:
      return assertUnreachableEvent(payload);
  }
}

/**
 * Batch helper for the hydration path: feed an array of EventLog
 * entries, get the flattened chat-message stream in time order.
 *
 * Stable sort by ``at`` keeps the ``askwhy`` (question, answer) pair
 * adjacent even if other events share the same timestamp.
 */
export function projectLog(entries: readonly EventLogEntry[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const entry of entries) {
    for (const msg of projectToMessages(entry)) out.push(msg);
  }
  out.sort((a, b) => a.at - b.at);
  return out;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Right-aligned meta for a resolved tool step, e.g. ``0.8s`` / ``12s``. */
function formatStepDuration(ms: number): string {
  if (ms < 0) return "";
  const s = ms / 1000;
  return s >= 10 ? `${Math.round(s)}s` : `${s.toFixed(1)}s`;
}

/** Build a one-line summary for a ``library`` event. Prefers the
 *  diff (Spec 24 T2) when present; falls back to a version delta. */
function librarySummaryFromPayload(payload: Extract<EventLogEntry["payload"], { kind: "library" }>): string {
  if (payload.diff && payload.diff.length > 0) {
    const adds = payload.diff.filter((d) => d.op === "added").length;
    const removes = payload.diff.filter((d) => d.op === "removed").length;
    const renames = payload.diff.filter((d) => d.op === "renamed").length;
    const parts: string[] = [];
    if (adds) parts.push(`+${adds}`);
    if (removes) parts.push(`-${removes}`);
    if (renames) parts.push(`~${renames}`);
    return parts.length > 0 ? `library updated (${parts.join(" ")})` : "library updated";
  }
  if (payload.fromVersion && payload.toVersion) {
    return `library ${payload.fromVersion} → ${payload.toVersion}`;
  }
  return "library updated";
}

/** Build the system-note text for a ``run_complete`` event. Pure;
 *  exported so the projection test pins the exact wording. Missing
 *  fields are dropped (no "undefined / 0" surfaced to writers). */
export function formatRunCompleteText(payload: Extract<EventLogEntry["payload"], { kind: "run_complete" }>): string {
  const fragments: string[] = [];
  const drafted = payload.draftedSections;
  const total = payload.totalSections;
  if (drafted != null && total != null) {
    fragments.push(`${drafted}/${total} drafted`);
  } else if (drafted != null) {
    fragments.push(`${drafted} drafted`);
  }
  if (payload.flaggedSections != null && payload.flaggedSections > 0) {
    fragments.push(`${payload.flaggedSections} flagged`);
  }
  if (payload.durationMs != null) {
    fragments.push(`in ${formatDuration(payload.durationMs)}`);
  }
  return fragments.length > 0 ? `Run complete — ${fragments.join(", ")}` : "Run complete";
}

/** Build the assistant ack for a ``doctype_selected`` event. Pure;
 *  exported so tests pin the exact wording.
 *
 *  Phase 9.5 (Bundle B+D) — the next-step pointer that used to be
 *  appended here moved to ``commandSuggester`` so guidance is
 *  state-aware (a separate ``agent_note`` event). Acks are now
 *  pure acknowledgements; the suggester decides what (if anything)
 *  comes next based on full state. */
export function formatDoctypeAck(label: string): string {
  return `Got it — ${label}.`;
}

/** Build the assistant ack for a ``library_selected`` event. Pure;
 *  exported so tests pin the exact wording. ``created`` flips the
 *  verb only; mirrors the doctype ack otherwise.
 *
 *  Phase 9.5 (Bundle B+D) — same refactor as ``formatDoctypeAck``:
 *  next-step copy moved to ``commandSuggester``. */
export function formatLibraryAck(libraryName: string, created: boolean): string {
  const verb = created ? "Created" : "Selected";
  return `${verb} "${libraryName}".`;
}

/** Pure ms → "Xm Ys" (or "Ys" when under a minute). */
function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/** Compile-time exhaustiveness check. If a new EventKind is added
 *  without a case above, TypeScript fails this call site (the
 *  parameter type is ``never`` only when every kind is handled). */
function assertUnreachableEvent(payload: never): ChatMessage[] {
  if (typeof console !== "undefined") {
    console.warn("[messageProjection] unhandled payload kind", payload);
  }
  return [];
}
